import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { fmtDateTime } from "@/lib/format";
import { Plug, CheckCircle2, RefreshCw, ExternalLink, Unplug, MapPin } from "lucide-react";
import { toast } from "sonner";
import SignupFormSettingsCard from "@/components/SignupFormSettingsCard";

import { TwoFactorCard } from "@/components/TwoFactorCard";
import { BrandingCard } from "@/components/BrandingCard";
import { company } from "@/lib/branding";

export default function Settings() {
    const { user } = useAuth();
    const { theme, setTheme } = useTheme();
    const [qbo, setQbo] = useState(null);
    const [working, setWorking] = useState(false);
    const [depot, setDepot] = useState(null);
    const [depotDraft, setDepotDraft] = useState("");
    const [depotSaving, setDepotSaving] = useState(false);

    const loadQbo = () => api.get("/quickbooks/status").then(({ data }) => setQbo(data));
    const loadDepot = () => api.get("/settings/depot").then(({ data }) => {
        setDepot(data); setDepotDraft(data.is_default ? "" : data.address);
    });
    useEffect(() => { loadQbo(); loadDepot(); }, []);

    const saveDepot = async () => {
        setDepotSaving(true);
        try {
            const { data } = await api.put("/settings/depot", { address: depotDraft });
            setDepot(data);
            toast.success(data.is_default
                ? "Depot reset to default"
                : "Depot address saved — future routes will start & end here");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally { setDepotSaving(false); }
    };

    const connectQbo = async () => {
        setWorking(true);
        try {
            const { data } = await api.post("/quickbooks/connect", { company_name: company() });
            if (data.status === "redirect_needed") {
                window.location.href = data.url;
                return;
            }
            toast.success(`Connected to ${data.company_name}${data.demo ? " (demo mode)" : ""}`);
            loadQbo();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Connect failed");
        } finally { setWorking(false); }
    };

    const disconnectQbo = async () => {
        if (!window.confirm("Disconnect QuickBooks? Synced records will keep their qbo ids.")) return;
        setWorking(true);
        try {
            await api.post("/quickbooks/disconnect");
            toast.success("Disconnected");
            setQbo({ configured: qbo?.configured, connected: false, demo_mode: qbo?.demo_mode,
                     realm_id: null, company_name: null, connected_at: null,
                     last_sync_at: null, stats: { customers: 0, invoices: 0 } });
            await loadQbo();
        } catch { toast.error("Failed"); }
        finally { setWorking(false); }
    };

    const syncAll = async () => {
        setWorking(true);
        try {
            const { data } = await api.post("/quickbooks/sync-all");
            toast.success(`Synced ${data.customers_synced} customers + ${data.invoices_synced} invoices`);
            loadQbo();
        } catch { toast.error("Sync failed"); }
        finally { setWorking(false); }
    };

    return (
        <div data-testid="settings-page">
            <PageHeader title="Settings" subtitle="Company profile, integrations and team." />
            <div className="px-8 py-6 grid lg:grid-cols-2 gap-4">
                {/* Signup form editor spans the whole row — it's the most
                    substantial editor on this page. */}
                {user?.role === "admin" && (
                    <div className="lg:col-span-2">
                        <SignupFormSettingsCard />
                    </div>
                )}
                <Card>
                    <CardContent className="p-6 space-y-4">
                        <div className="overline">Profile</div>
                        <h3 className="font-heading text-lg font-semibold">Your account</h3>
                        <div className="grid grid-cols-2 gap-3">
                            <div><Label className="text-xs">Name</Label><Input value={user?.name || ""} readOnly /></div>
                            <div><Label className="text-xs">Role</Label><Input value={user?.role || ""} readOnly /></div>
                            <div className="col-span-2"><Label className="text-xs">Email</Label><Input value={user?.email || ""} readOnly /></div>
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardContent className="p-6 space-y-4">
                        <div className="overline">Appearance</div>
                        <h3 className="font-heading text-lg font-semibold">Theme</h3>
                        <div className="flex items-center justify-between p-3 border border-border rounded-md">
                            <div>
                                <div className="text-sm font-medium">Dark mode</div>
                                <div className="text-xs text-muted-foreground">Switch between light and dark interface</div>
                            </div>
                            <Switch checked={theme === "dark"} onCheckedChange={(v) => setTheme(v ? "dark" : "light")}
                                    data-testid="settings-theme-toggle" />
                        </div>
                    </CardContent>
                </Card>

                {/* Depot / route starting point */}
                <BrandingCard />
                <TwoFactorCard />
                <Card className="lg:col-span-2" data-testid="depot-card">
                    <CardContent className="p-6 space-y-3">
                        <div className="flex items-start justify-between">
                            <div>
                                <div className="overline mb-1">Route optimization</div>
                                <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
                                    <MapPin className="w-4 h-4" />Depot / route starting point
                                </h3>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Every truck starts and ends its day here. Uber-style TSP optimization
                                    orders your stops to minimize drive time round-trip from this address.
                                </p>
                            </div>
                            {depot && !depot.is_default && (
                                <span className="text-[11px] font-medium uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
                                    Custom
                                </span>
                            )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
                            <div>
                                <Label className="text-xs mb-1 block">Depot address</Label>
                                <Input
                                    value={depotDraft}
                                    placeholder={depot?.address || "2600 County Road 223, Florence, TX 76527"}
                                    onChange={(e) => setDepotDraft(e.target.value)}
                                    data-testid="depot-address-input"
                                />
                                <div className="text-[11px] text-muted-foreground mt-1">
                                    Leave blank to reset to the default. Currently:&nbsp;
                                    <span className="font-medium text-foreground">{depot?.address || "loading…"}</span>
                                </div>
                            </div>
                            <Button onClick={saveDepot} disabled={depotSaving} data-testid="depot-save-btn">
                                {depotSaving ? "Saving…" : "Save depot"}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* QuickBooks integration card */}                <Card className="lg:col-span-2" data-testid="qbo-card">
                    <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                            <div>
                                <div className="overline mb-1">Accounting integration</div>
                                <h3 className="font-heading text-lg font-semibold">QuickBooks Online</h3>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Push customers and invoices into QuickBooks so your bookkeeper doesn't have to re-key anything.
                                </p>
                            </div>
                            {qbo?.connected ? (
                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-medium">
                                    <CheckCircle2 className="w-3.5 h-3.5" />Connected
                                </div>
                            ) : (
                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-muted-foreground text-xs font-medium">
                                    Not connected
                                </div>
                            )}
                        </div>

                        {qbo?.connected ? (
                            <div className="space-y-4">
                                <div className="grid grid-cols-3 gap-3">
                                    <Stat label="Company" value={qbo.company_name} />
                                    <Stat label="Customers synced" value={qbo.stats?.customers || 0} />
                                    <Stat label="Invoices synced" value={qbo.stats?.invoices || 0} />
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {qbo.demo_mode && <><span className="font-semibold text-amber-600 dark:text-amber-400">DEMO MODE</span> — add INTUIT_CLIENT_ID + INTUIT_CLIENT_SECRET to env for real OAuth. </>}
                                    Last sync: {qbo.last_sync_at ? fmtDateTime(qbo.last_sync_at) : "—"}
                                </div>
                                <div className="flex gap-2">
                                    <Button onClick={syncAll} disabled={working} data-testid="qbo-sync-btn">
                                        <RefreshCw className="w-4 h-4 mr-1" />{working ? "Syncing…" : "Sync now"}
                                    </Button>
                                    <Button variant="outline" onClick={disconnectQbo} disabled={working} data-testid="qbo-disconnect-btn">
                                        <Unplug className="w-4 h-4 mr-1" />Disconnect
                                    </Button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <div className="text-xs text-muted-foreground p-3 rounded-md bg-secondary/50 border border-border">
                                    {qbo?.configured
                                        ? "Click to authorize via Intuit OAuth."
                                        : <>OAuth credentials not configured. Click to connect in <strong>demo mode</strong> (records are stamped with a demo qbo_id so you can preview the sync flow). For production, add <code className="font-mono text-[11px]">INTUIT_CLIENT_ID</code> and <code className="font-mono text-[11px]">INTUIT_CLIENT_SECRET</code> to <code className="font-mono text-[11px]">backend/.env</code>.</>
                                    }
                                </div>
                                <Button onClick={connectQbo} disabled={working} data-testid="qbo-connect-btn">
                                    <Plug className="w-4 h-4 mr-1" />{working ? "Connecting…" : qbo?.configured ? "Connect QuickBooks" : "Connect (demo)"}
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Other integrations */}
                <Card className="lg:col-span-2">
                    <CardContent className="p-6 space-y-4">
                        <div className="overline">Other integrations</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {[
                                { name: "Stripe", desc: "Online payments + AutoPay", status: "Connected (test key)", tone: "success" },
                                { name: "Google Maps", desc: "Address autocomplete + dispatch map", status: "Connected", tone: "success" },
                                { name: "Twilio SMS", desc: "Broadcast SMS + reminders", status: "Connected", tone: "success" },
                                { name: "SendGrid", desc: "Transactional email", status: "Not configured", tone: "muted" },
                                { name: "Driver Mobile App", desc: "Offline-capable + GPS streaming", status: "Built-in", tone: "success" },
                                { name: "Website Embed", desc: "WordPress + JS snippet", status: "Built-in", tone: "success" },
                            ].map((i) => (
                                <div key={i.name} className="border border-border rounded-md p-4 flex items-center justify-between">
                                    <div>
                                        <div className="font-medium text-sm">{i.name}</div>
                                        <div className="text-xs text-muted-foreground">{i.desc}</div>
                                    </div>
                                    <span className={`text-[11px] font-medium uppercase tracking-wide ${
                                        i.tone === "success" ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"
                                    }`}>{i.status}</span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="p-3 border border-border rounded-md">
            <div className="overline mb-1">{label}</div>
            <div className="font-heading text-lg font-bold truncate">{value || "—"}</div>
        </div>
    );
}
